/**
 * 🏢 프론트엔드 라우터 통합
 *
 * 🎯 핵심 철학:
 * - 모든 도메인 라우터 통합 관리
 * - 테넌트별 라우팅
 *
 * 공개 라우트 (/login, /onboarding, /genesis)는 src/app.tsx 에서 TenantProvider 밖에 정의됨.
 *
 * Bundle note (FRONTEND-BUNDLE-VENDOR-ANTD-AND-DOMAIN-ROUTER-CHUNK-V0):
 * lazyNamed imports target per-domain router modules — not the barrel
 * `configDrivenDomainRouters` — so Rollup emits separate async chunks.
 */

import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { BusinessRouteTransition } from '../components/common/BusinessRouteTransition';
import { BusinessPathGuard } from '../guards/BusinessPathGuard';
import { lazyDefault, lazyNamed } from '../routing/lazyRoute';
import { BUSINESS_PLANE_ROOT_ALIAS_ROUTES } from './businessPlaneRootAliases';

const EntityRouter = lazyNamed(
  () => import('../routing/config-driven/entityRouter'),
  'EntityRouter'
);
const ItemRouter = lazyNamed(() => import('../routing/config-driven/itemRouter'), 'ItemRouter');
const FinanceRouter = lazyNamed(
  () => import('../routing/config-driven/financeRouter'),
  'FinanceRouter'
);
const FinanceAdvancedRouter = lazyNamed(
  () => import('../routing/config-driven/financeAdvancedRouter'),
  'FinanceAdvancedRouter'
);
const SpaceRouter = lazyNamed(() => import('../routing/config-driven/spaceRouter'), 'SpaceRouter');
const KnowledgeRouter = lazyNamed(
  () => import('../routing/config-driven/knowledgeRouter'),
  'KnowledgeRouter'
);
const AppealRouter = lazyNamed(
  () => import('../routing/config-driven/appealRouter'),
  'AppealRouter'
);
const CalendarRouter = lazyNamed(
  () => import('../routing/config-driven/calendarRouter'),
  'CalendarRouter'
);
const AutomationRouter = lazyNamed(
  () => import('../routing/config-driven/automationRouter'),
  'AutomationRouter'
);
const LogRouter = lazyNamed(() => import('../routing/config-driven/logRouter'), 'LogRouter');
const FlowRouter = lazyNamed(() => import('../routing/config-driven/flowRouter'), 'FlowRouter');
const ApprovalsRouter = lazyNamed(
  () => import('../routing/config-driven/approvalsRouter'),
  'ApprovalsRouter'
);
const EventRouter = lazyNamed(() => import('../routing/config-driven/eventRouter'), 'EventRouter');
const ConsciousRouter = lazyNamed(
  () => import('../routing/compatDomainRouters'),
  'ConsciousRouter'
);
const ManufacturingRouter = lazyNamed(
  () => import('../routing/config-driven/manufacturingRouter'),
  'ManufacturingRouter'
);
const WarehouseRouter = lazyNamed(
  () => import('../routing/config-driven/warehouseRouter'),
  'WarehouseRouter'
);
const InventoryRouter = lazyNamed(
  () => import('../routing/config-driven/inventoryRouter'),
  'InventoryRouter'
);
const SystemRouter = lazyNamed(
  () => import('../routing/config-driven/systemRouter'),
  'SystemRouter'
);
const WorkspaceRouter = lazyNamed(
  () => import('../routing/config-driven/workspaceRouter'),
  'WorkspaceRouter'
);
const PhysicalAccessRouter = lazyNamed(
  () => import('../routing/config-driven/physicalAccessRouter'),
  'PhysicalAccessRouter'
);
const SettingsRouter = lazyNamed(
  () => import('../routing/config-driven/settingsRouter'),
  'SettingsRouter'
);
const ExportRouter = lazyNamed(
  () => import('../routing/config-driven/exportRouter'),
  'ExportRouter'
);

const DynamicTableTestPage = import.meta.env.DEV
  ? lazyDefault(() => import('../pages/test/DynamicTableTestPage'))
  : null;
const BusinessNotFoundPage = lazyNamed(
  () => import('../pages/BusinessNotFoundPage'),
  'BusinessNotFoundPage'
);
const QrScanResolvePage = lazyNamed(
  () => import('../pages/scan/QrScanResolvePage'),
  'QrScanResolvePage'
);

interface MainRouterProps {
  tenantId: string;
}

export const MainRouter: React.FC<MainRouterProps> = ({ tenantId }) => {
  return (
    <BusinessPathGuard>
      <BusinessRouteTransition>
        <Routes>
          {BUSINESS_PLANE_ROOT_ALIAS_ROUTES}

          <Route path="/q/:accessKey" element={<QrScanResolvePage tenantId={tenantId} />} />

          <Route path="/entity/*" element={<EntityRouter tenantId={tenantId} />} />

          {import.meta.env.DEV && DynamicTableTestPage ? (
            <Route path="/test/dynamic-table" element={<DynamicTableTestPage />} />
          ) : null}

          <Route path="/item/*" element={<ItemRouter tenantId={tenantId} />} />
          <Route path="/warehouse/*" element={<WarehouseRouter tenantId={tenantId} />} />
          <Route path="/inventory/*" element={<InventoryRouter tenantId={tenantId} />} />
          <Route path="/finance/*" element={<FinanceRouter tenantId={tenantId} />} />
          <Route
            path="/finance-advanced/*"
            element={<FinanceAdvancedRouter tenantId={tenantId} />}
          />
          <Route path="/space/*" element={<SpaceRouter tenantId={tenantId} />} />
          <Route path="/knowledge/*" element={<KnowledgeRouter tenantId={tenantId} />} />
          <Route path="/appeal/*" element={<AppealRouter tenantId={tenantId} />} />
          <Route path="/calendar/*" element={<CalendarRouter tenantId={tenantId} />} />
          <Route path="/automation/*" element={<AutomationRouter tenantId={tenantId} />} />
          <Route path="/log/*" element={<LogRouter tenantId={tenantId} />} />
          <Route path="/flow/*" element={<FlowRouter tenantId={tenantId} />} />
          <Route path="/approvals/*" element={<ApprovalsRouter tenantId={tenantId} />} />
          <Route path="/event/*" element={<EventRouter tenantId={tenantId} />} />
          <Route path="/conscious/*" element={<ConsciousRouter tenantId={tenantId} />} />
          <Route path="/manufacturing/*" element={<ManufacturingRouter tenantId={tenantId} />} />

          <Route path="/system/*" element={<SystemRouter tenantId={tenantId} />} />
          <Route path="/physical-access/*" element={<PhysicalAccessRouter tenantId={tenantId} />} />
          <Route path="/settings/*" element={<SettingsRouter tenantId={tenantId} />} />

          <Route path="/export/*" element={<ExportRouter tenantId={tenantId} />} />
          <Route path="/workspace/*" element={<WorkspaceRouter tenantId={tenantId} />} />

          <Route path="*" element={<BusinessNotFoundPage />} />
        </Routes>
      </BusinessRouteTransition>
    </BusinessPathGuard>
  );
};

export default MainRouter;
